<script setup>

import {ErrorMessage, Field, Form} from 'vee-validate'
import {computed, reactive, ref} from 'vue'
import axios from 'axios'
import {useAddressField} from '../../composables/useAddressField'
import GoogleAddress from './../Google/GoogleAddress.vue'
import {useFormSubmit} from '../../composables/useFormSubmit'

const { showServerSideValidateErrors, enquirySubmitDataLayer, sendFBLeadData } = useFormSubmit()
const {showAddressFields, addressValueChange, showCustomAddressLink, toggleCustomAddress} = useAddressField()

const props = defineProps({
  villageName: {
    type: String,
    default: '',
  },
})

const validationSchema = computed(() => {
  let schema = {
    first_name: 'required',
    last_name: 'required',
    email: 'required|email',
    phone_number: 'required|phoneNumber',
  }

  if (showAddressFields.value) {
    schema.street_address = 'required'
    schema.postcode = 'required|numeric'
    schema.city = 'required'
  }

  return schema
})

const confirmation = reactive({show: false, message: '', class: ''})
let isSubmitting = ref(false)
const showEnquirySuccess = ref(false)

const onSubmit = async () => {
  isSubmitting.value = true
  confirmation.show = true
  confirmation.class = 'message--warning'
  confirmation.message = 'Submitting....'
  const form  = document.getElementById('villageContactFormMobile');
  let formData = new FormData(form);
  window.grecaptcha.execute(document.getElementById('mySiteKeyVariable').value, {
    action: formData.get('captcha_action'),
  }).then((token) => {
    const submitButton = form.querySelector('.submit');
    submitButton.disabled = true;
    formData.set('g-recaptcha-response', token);
    axios.post('/enquiry-submitted', formData).then(function (response) {
      enquirySubmitDataLayer(form);
      sendFBLeadData(formData.get('subject'));
      // Reset the form
      form.reset();
      showEnquirySuccess.value = true;
    }).catch(error => {
      submitButton.disabled = false;
      showServerSideValidateErrors(error, isSubmitting, confirmation)
    });
  });
}
</script>

<template>
  <Form
    :validation-schema="validationSchema"
    v-slot="{ errors }"
    @submit="onSubmit"
    id="villageContactFormMobile"
  >
    <slot name="token"></slot>
    <div class="village__form">
      <div class="form-group form-group--input mt-4">
        <label for="first_name">First name <span class="text-red">*</span></label>
        <Field
          id="first_name"
          name="first_name"
          class="form-control"
          :class="{'is-danger': errors['first_name'] }"
        />
        <ErrorMessage name="first_name" class="is-danger" />
      </div>
      <div class="form-group form-group--input mt-4">
        <label for="last_name">Last name <span class="text-red">*</span></label>
        <Field
          name="last_name"
          class="form-control"
          :class="{'is-danger': errors['last_name'] }"
          id="last_name"
        />
        <ErrorMessage name="last_name" class="is-danger" />
      </div>
      <div class="form-group form-group--input mt-4">
        <label for="email">Email <span class="text-red">*</span></label>
        <Field
          id="email"
          name="email"
          class="form-control"
          :class="{'is-danger': errors['email'] }"
        />
        <ErrorMessage name="email" class="is-danger" />
      </div>
      <div class="form-group form-group--input mt-4">
        <label for="phone_number">Phone number <span class="text-red">*</span></label>
        <Field
          id="phone_number"
          name="phone_number"
          class="form-control"
          :class="{'is-danger': errors['phone_number'] }"
        />
        <ErrorMessage name="phone_number" class="is-danger" />
      </div>

      <div v-if="showAddressFields">
        <div class="form-group form-group--input mt-4">
          <label for="street_address">Street address <span class="text-red">*</span></label>
          <Field
            id="street_address"
            name="street_address"
            placeholder="Street Address *"
            class="form-control"
            :class="{'is-danger': errors['street_address'] }"
          />
          <ErrorMessage name="street_address" class="is-danger" />
        </div>
        <div class="form-group form-group--input mt-4">
          <label for="city">City <span class="text-red">*</span></label>
          <Field
            id="city"
            name="city"
            class="form-control"
            :class="{'is-danger': errors['city'] }"
          />
          <ErrorMessage name="city" class="is-danger" />
        </div>
        <div class="form-group form-group--input mt-4">
          <label for="postcode">Postcode <span class="text-red">*</span></label>
          <Field
            id="postcode"
            name="postcode"
            class="form-control"
            :class="{'is-danger': errors['postcode'] }"
          />
          <ErrorMessage name="postcode" class="is-danger" />
        </div>
        <div class="form-group form-group--input mt-4">
          <slot name="country"></slot>
        </div>
      </div>
      <div class="mt-4">
        <div class="form-group form-group--input" v-if="!showAddressFields">
          <label for="address" class="sr-only">Address</label>
          <google-address
            :address-id="'village-page-enquiry'"
            v-on:addressvaluechange="addressValueChange"
            :classname="'form-control'"
          ></google-address>
          <div v-if="showCustomAddressLink" class="mt-2 fs-14 font-weight-600" >
            Can't find your address? <a href="#" @click.prevent="toggleCustomAddress" id="enterCustomAddress">Enter it here</a>
          </div>
        </div>
      </div>
      <div class="form-group form-group--input mt-4">
        <label for="comment">Message</label>
        <textarea
          id="message"
          rows="6"
          class="form-control"
          name="message"
          :placeholder="`Please send me an information pack for ${props.villageName}`"
        ></textarea>
      </div>
      <div class="checkbox-custom mt-4 mb-4">
        <input type="checkbox" class="subscribe" id="subscribe" name="subscribe" value="1" checked>
        <label class="form-check-label" for="subscribe">I would like to receive emails with village info, updates and more from Village Guide</label>
      </div>
      <input type="hidden" name="captcha_action" value="village_enquiry">
      <transition name="fade">
        <div :class="confirmation.class" class="message" v-if="confirmation.show">{{ confirmation.message }}</div>
      </transition>
      <slot name="hiddenfields"></slot>
      <div class="row">
        <div class="col-lg-12 text-center">
          <input type="submit" value="Send Message" class="btn btn--green btn--large submit mb-4 mt-0">
        </div>
      </div>
      <div class="pb-8">
        <slot name="googlerecaptchatext"></slot>
      </div>
    </div>
  </Form>
</template>
